export interface NotificationInstallModeInput {
  standaloneDisplayMode: boolean;
  navigatorStandalone: boolean;
  userAgent: string;
  platform: string;
  maxTouchPoints: number;
}

export interface NotificationInstallMode {
  standalone: boolean;
  iPhoneLike: boolean;
  requiresInstall: boolean;
  runtimeEligible: boolean;
}

interface InstallGuidanceContextInput {
  userAgent: string;
  platform: string;
  maxTouchPoints: number;
}

interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean;
}

function normalizeText(value: string): string {
  return value.trim().toLowerCase();
}

function normalizeTouchPoints(value: number): number {
  if (!Number.isFinite(value) || value < 0) {
    return 0;
  }
  return Math.trunc(value);
}

export function detectIPhoneInstallGuidanceContext(input: InstallGuidanceContextInput): boolean {
  const userAgent = normalizeText(input.userAgent);
  const platform = normalizeText(input.platform);
  const maxTouchPoints = normalizeTouchPoints(input.maxTouchPoints);

  if (/iphone|ipad|ipod/.test(userAgent)) {
    return true;
  }

  // iPadOS may report MacIntel platform while exposing touch points.
  return platform === "macintel" && maxTouchPoints > 1;
}

export function resolveNotificationInstallMode(input: NotificationInstallModeInput): NotificationInstallMode {
  const standalone = input.standaloneDisplayMode || input.navigatorStandalone;
  const iPhoneLike = detectIPhoneInstallGuidanceContext({
    userAgent: input.userAgent,
    platform: input.platform,
    maxTouchPoints: input.maxTouchPoints,
  });
  const requiresInstall = iPhoneLike && !standalone;

  return {
    standalone,
    iPhoneLike,
    requiresInstall,
    runtimeEligible: !requiresInstall,
  };
}

function readStandaloneDisplayMode(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  try {
    return window.matchMedia("(display-mode: standalone)").matches;
  } catch {
    return false;
  }
}

function readNavigatorStandalone(): boolean {
  if (typeof navigator === "undefined") {
    return false;
  }
  return (navigator as NavigatorWithStandalone).standalone === true;
}

export function readNotificationInstallMode(): NotificationInstallMode {
  if (typeof navigator === "undefined") {
    return {
      standalone: false,
      iPhoneLike: false,
      requiresInstall: false,
      runtimeEligible: true,
    };
  }

  return resolveNotificationInstallMode({
    standaloneDisplayMode: readStandaloneDisplayMode(),
    navigatorStandalone: readNavigatorStandalone(),
    userAgent: navigator.userAgent || "",
    platform: navigator.platform || "",
    maxTouchPoints: navigator.maxTouchPoints || 0,
  });
}
